import {
  coasterFamilyForProject,
  compileProjectTrack,
  initialTrainReferenceDistance,
  stationTrainLayout,
} from '../app/ride-track.ts';
import { runRideToCompletion, type RidePeak, type RideSummary } from '../ride/runner.ts';
import type {
  AuthoritativeValidationSummary,
  CoasterProject,
  ValidationOutcome,
} from '../schema/index.ts';
import { hashCoasterTrack } from '../schema/index.ts';
import { TrackCompiler } from '../track/index.ts';
import {
  intensitySummary,
  promoteIntensity,
  summarizeRideIntensity,
  type AuthoritativeIntensitySummary,
  type IntensityThresholdConfig,
  type SimulatedIntensityLevel,
} from '../app/ride-intensity.ts';

export type FullValidationSeverity = 'info' | 'warning' | 'severe' | 'blocker';
export type TestRidePolicy = 'normal' | 'diagnostic_only' | 'blocked';

export interface ThresholdBands {
  readonly noticeable: number;
  readonly intense: number;
  readonly extreme: number;
  readonly absurd: number;
}

export interface ValidationThresholdConfig {
  readonly version: string;
  readonly speed: ThresholdBands;
  readonly positiveVerticalG: ThresholdBands;
  readonly negativeVerticalG: ThresholdBands;
  readonly absoluteLateralG: ThresholdBands;
  readonly absoluteLongitudinalG: ThresholdBands;
}

export const PHASE_3_VALIDATION_THRESHOLDS: ValidationThresholdConfig = {
  version: 'weighted-intensity-2026-07-v3',
  speed: { noticeable: 45, intense: 60, extreme: 70, absurd: 100 },
  positiveVerticalG: { noticeable: 4, intense: 5, extreme: 6, absurd: 8 },
  negativeVerticalG: { noticeable: -0.5, intense: -1, extreme: -1.5, absurd: -3 },
  absoluteLateralG: { noticeable: 0.75, intense: 1.25, extreme: 2, absurd: 4 },
  absoluteLongitudinalG: { noticeable: 0.75, intense: 1.25, extreme: 2, absurd: 4 },
};

export interface FullValidationIssue {
  readonly id: string;
  readonly code: string;
  readonly severity: FullValidationSeverity;
  readonly category: 'technical' | 'intensity';
  readonly intensityTier?: Exclude<SimulatedIntensityLevel, 'gentle'>;
  readonly message: string;
  readonly sectionId?: string;
  readonly distanceM?: number;
  readonly carIndex?: number;
  readonly measured?: number;
  readonly threshold?: number;
  readonly unit?: string;
  readonly durationMs?: number;
}

export interface FullValidationMetrics {
  readonly outcome: RideSummary['outcome'];
  readonly durationSeconds: number;
  readonly progressPercent: number;
  readonly topSpeedMps: number;
  readonly maxVerticalG: number;
  readonly minVerticalG: number;
  readonly maxAbsLateralG: number;
  readonly maxAbsLongitudinalG: number;
}

export interface FullRunValidationResult {
  readonly validatorVersion: 'phase3-track-bound-v2';
  readonly thresholdVersion: string;
  readonly trackHash: string;
  readonly issues: readonly FullValidationIssue[];
  readonly metrics: FullValidationMetrics;
  readonly intensity: AuthoritativeIntensitySummary;
  readonly testPolicy: TestRidePolicy;
  readonly technicalEligible: boolean;
  /** @deprecated Compatibility alias until the Phase 4 publishing adapter lands. */
  readonly publishReady: boolean;
  readonly summary: RideSummary;
}

function schemaOutcome(outcome: RideSummary['outcome']): ValidationOutcome {
  switch (outcome) {
    case 'completed':
      return 'completed';
    case 'stalled':
      return 'stalled';
    case 'arrival_failed':
      return 'station_arrival_failed';
    case 'timed_out':
      return 'timeout';
    case 'blocked':
      return 'numerical_failure';
    default:
      return 'invalid_track';
  }
}

export function toAuthoritativeValidationSummary(
  project: CoasterProject,
  result: FullRunValidationResult,
  lastRunAt = new Date().toISOString(),
): AuthoritativeValidationSummary {
  const status = result.issues.some(
    (issue) => issue.category === 'technical' && issue.severity === 'blocker',
  )
    ? 'blocked'
    : result.issues.some((issue) => issue.severity === 'warning' || issue.severity === 'severe')
      ? 'warnings'
      : 'valid';
  return {
    status,
    lastRunAt,
    validatorVersion: result.validatorVersion,
    thresholdPolicyVersion: result.thresholdVersion,
    trackHash: result.trackHash,
    outcome: schemaOutcome(result.summary.outcome),
    completionPercent: result.metrics.progressPercent,
    durationSeconds: result.metrics.durationSeconds,
    metrics: {
      topSpeedMps: result.metrics.topSpeedMps,
      maxVerticalG: result.metrics.maxVerticalG,
      minVerticalG: result.metrics.minVerticalG,
      maxAbsLateralG: result.metrics.maxAbsLateralG,
      maxAbsLongitudinalG: result.metrics.maxAbsLongitudinalG,
    },
    issues: result.issues.map((issue) => ({
      id: issue.id,
      severity: issue.severity,
      code: issue.code,
      message: issue.message,
      element: issue.sectionId
        ? { kind: 'track_section' as const, id: issue.sectionId }
        : { kind: 'project' as const, id: project.projectId },
      ...(issue.measured === undefined || issue.unit === undefined
        ? {}
        : { metric: { name: issue.code, value: issue.measured, unit: issue.unit } }),
      location:
        issue.distanceM === undefined
          ? null
          : {
              distanceMeters: Math.max(0, issue.distanceM),
              sectionId: issue.sectionId ?? null,
              nodeId: null,
            },
      carIndex: issue.carIndex ?? null,
      durationSeconds: issue.durationMs === undefined ? null : issue.durationMs / 1_000,
      threshold:
        issue.threshold === undefined || issue.unit === undefined
          ? null
          : {
              value: issue.threshold,
              unit: issue.unit,
              comparison: issue.code === 'negative_vertical_g' ? 'below' : 'above',
            },
    })),
  };
}

function intensityHigh(
  value: number,
  bands: ThresholdBands,
): Exclude<SimulatedIntensityLevel, 'gentle'> | null {
  if (value >= bands.absurd) return 'extreme';
  if (value >= bands.extreme) return 'intense';
  if (value >= bands.intense) return 'thrilling';
  if (value >= bands.noticeable) return 'moderate';
  return null;
}

function intensityLow(
  value: number,
  bands: ThresholdBands,
): Exclude<SimulatedIntensityLevel, 'gentle'> | null {
  if (value <= bands.absurd) return 'extreme';
  if (value <= bands.extreme) return 'intense';
  if (value <= bands.intense) return 'thrilling';
  if (value <= bands.noticeable) return 'moderate';
  return null;
}

function issueSeverity(level: SimulatedIntensityLevel): FullValidationSeverity {
  return level === 'moderate' ? 'info' : level === 'thrilling' ? 'warning' : 'severe';
}

function sectionAt(project: CoasterProject, distance: number): string | undefined {
  try {
    return compileProjectTrack(project, new TrackCompiler()).sampleByDistance(distance).section?.id;
  } catch {
    return undefined;
  }
}

function metricIssue(
  project: CoasterProject,
  code: string,
  intensityTier: Exclude<SimulatedIntensityLevel, 'gentle'>,
  message: string,
  peak: RidePeak,
  threshold: number,
  unit: string,
): FullValidationIssue {
  const distanceBucket = Math.round(peak.distance * 10);
  return {
    id: `full:${code}:${distanceBucket}:car:${peak.carIndex}`,
    code,
    severity: issueSeverity(intensityTier),
    category: 'intensity',
    intensityTier,
    message,
    sectionId: sectionAt(project, peak.distance),
    distanceM: peak.distance,
    carIndex: peak.carIndex,
    measured: peak.value,
    threshold,
    unit,
  };
}

function thresholdForIntensity(
  level: Exclude<SimulatedIntensityLevel, 'gentle'>,
  bands: ThresholdBands,
): number {
  return level === 'extreme'
    ? bands.absurd
    : level === 'intense'
      ? bands.extreme
      : level === 'thrilling'
        ? bands.intense
        : bands.noticeable;
}

function longestSustainedSeconds(
  trace: RideSummary['trace'],
  predicate: (sample: RideSummary['trace'][number]) => boolean,
): number {
  let start: number | null = null;
  let longest = 0;
  for (const sample of trace) {
    if (predicate(sample)) {
      start ??= sample.time;
      longest = Math.max(longest, sample.time - start);
    } else start = null;
  }
  return longest;
}

export async function validateFullRide(
  project: CoasterProject,
  thresholds = PHASE_3_VALIDATION_THRESHOLDS,
): Promise<FullRunValidationResult> {
  const sampler = compileProjectTrack(project, new TrackCompiler());
  const stationLayout = stationTrainLayout(project, sampler);
  const summary = runRideToCompletion({
    sampler,
    family: coasterFamilyForProject(project),
    initialDistance: stationLayout?.parkDistance ?? initialTrainReferenceDistance(project, sampler),
    stationParkDistance: stationLayout?.parkDistance,
    maxDurationSeconds: 180,
  });
  const issues: FullValidationIssue[] = summary.issues.map((candidate, index) => ({
    id: `full:${candidate.code}:${index}`,
    code: candidate.code,
    severity: candidate.severity === 'block' ? 'blocker' : candidate.severity,
    category: 'technical',
    message: candidate.messageKey,
    sectionId: candidate.sectionId,
    distanceM: candidate.distance,
    carIndex: candidate.carIndex,
    measured: candidate.measured,
    threshold: candidate.threshold,
  }));

  if (stationLayout && !stationLayout.fits) {
    issues.push({
      id: 'full:station_train_capacity',
      code: 'station_train_capacity',
      severity: 'blocker',
      category: 'technical',
      message: `The station is ${stationLayout.lengthMeters.toFixed(1)} m long, but this train requires ${stationLayout.requiredLengthMeters.toFixed(1)} m to park fully inside it.`,
      measured: stationLayout.lengthMeters,
      threshold: stationLayout.requiredLengthMeters,
      unit: 'm',
    });
  }

  if (summary.outcome !== 'completed') {
    const code = summary.outcome === 'stalled' ? 'ride_stall' : `ride_${summary.outcome}`;
    const last = summary.trace.at(-1);
    issues.push({
      id: `full:${code}`,
      code,
      severity: 'blocker',
      category: 'technical',
      message:
        summary.outcome === 'stalled'
          ? 'The train stalls before completing the circuit.'
          : summary.outcome === 'arrival_failed'
            ? 'The train passes through the station too fast to finish the ride.'
            : summary.outcome === 'timed_out'
              ? 'The train does not complete within the validation time limit.'
              : 'The full ride could not complete.',
      distanceM: last?.distance,
      sectionId: last ? sampler.sampleByDistance(last.distance).section?.id : undefined,
      measured: summary.progressPercent,
      threshold: 100,
      unit: '%',
    });
  }

  const addHigh = (
    code: string,
    label: string,
    peak: RidePeak,
    bands: ThresholdBands,
    unit: string,
  ) => {
    const tier = intensityHigh(Math.abs(peak.value), bands);
    if (!tier) return;
    issues.push(
      metricIssue(
        project,
        code,
        tier,
        `${label} reaches the ${intensitySummary(tier).label.toLowerCase()} range.`,
        { ...peak, value: Math.abs(peak.value) },
        thresholdForIntensity(tier, bands),
        unit,
      ),
    );
  };
  addHigh('speed', 'Top speed', summary.topSpeed, thresholds.speed, 'm/s');
  const positiveTier = intensityHigh(summary.maxVerticalG.value, thresholds.positiveVerticalG);
  if (positiveTier)
    issues.push(
      metricIssue(
        project,
        'positive_vertical_g',
        positiveTier,
        `Positive vertical G reaches the ${intensitySummary(positiveTier).label.toLowerCase()} range.`,
        summary.maxVerticalG,
        thresholdForIntensity(positiveTier, thresholds.positiveVerticalG),
        'G',
      ),
    );
  const negativeTier = intensityLow(summary.minVerticalG.value, thresholds.negativeVerticalG);
  if (negativeTier)
    issues.push(
      metricIssue(
        project,
        'negative_vertical_g',
        negativeTier,
        `Negative vertical G reaches the ${intensitySummary(negativeTier).label.toLowerCase()} range.`,
        summary.minVerticalG,
        thresholdForIntensity(negativeTier, thresholds.negativeVerticalG),
        'G',
      ),
    );
  addHigh('lateral_g', 'Lateral G', summary.maxAbsLateralG, thresholds.absoluteLateralG, 'G');
  addHigh(
    'longitudinal_g',
    'Longitudinal G',
    summary.maxAbsLongitudinalG,
    thresholds.absoluteLongitudinalG,
    'G',
  );

  for (let index = 0; index < issues.length; index += 1) {
    const issue = issues[index]!;
    if (issue.category !== 'intensity' || issue.threshold === undefined || !issue.intensityTier)
      continue;
    const durationSeconds = longestSustainedSeconds(summary.trace, (sample) => {
      if (issue.code === 'speed') return Math.abs(sample.speed) >= Math.abs(issue.threshold!);
      if (issue.code === 'positive_vertical_g') return sample.verticalG >= issue.threshold!;
      if (issue.code === 'negative_vertical_g') return sample.verticalG <= issue.threshold!;
      if (issue.code === 'lateral_g')
        return Math.abs(sample.lateralG) >= Math.abs(issue.threshold!);
      if (issue.code === 'longitudinal_g')
        return Math.abs(sample.longitudinalG) >= Math.abs(issue.threshold!);
      return false;
    });
    if (durationSeconds >= 2)
      issues[index] = {
        ...issue,
        intensityTier: promoteIntensity(issue.intensityTier) as Exclude<
          SimulatedIntensityLevel,
          'gentle'
        >,
        severity: issueSeverity(promoteIntensity(issue.intensityTier)),
        message: `${issue.message} It persists for ${durationSeconds.toFixed(1)} seconds.`,
        durationMs: durationSeconds * 1_000,
      };
  }

  const technicalBlocker = issues.some(
    (candidate) => candidate.category === 'technical' && candidate.severity === 'blocker',
  );
  const structuralBlock = issues.some((candidate) =>
    [
      'invalid_state',
      'solver_divergence',
      'sampling_failure',
      'non_finite_sample',
      'unsupported_section',
    ].includes(candidate.code),
  );
  const testPolicy: TestRidePolicy = structuralBlock
    ? 'blocked'
    : technicalBlocker
      ? 'diagnostic_only'
      : 'normal';
  const technicalEligible = summary.outcome === 'completed' && !technicalBlocker;
  return {
    validatorVersion: 'phase3-track-bound-v2',
    thresholdVersion: thresholds.version,
    trackHash: await hashCoasterTrack(project),
    issues: issues.sort((a, b) => {
      const order = { blocker: 0, severe: 1, warning: 2, info: 3 } as const;
      return (
        Number(b.category === 'technical') - Number(a.category === 'technical') ||
        order[a.severity] - order[b.severity] ||
        a.id.localeCompare(b.id)
      );
    }),
    metrics: {
      outcome: summary.outcome,
      durationSeconds: summary.durationSeconds,
      progressPercent: summary.progressPercent,
      topSpeedMps: summary.topSpeed.value,
      maxVerticalG: summary.maxVerticalG.value,
      minVerticalG: summary.minVerticalG.value,
      maxAbsLateralG: Math.abs(summary.maxAbsLateralG.value),
      maxAbsLongitudinalG: Math.abs(summary.maxAbsLongitudinalG.value),
    },
    intensity: summarizeRideIntensity(summary, thresholds as IntensityThresholdConfig),
    testPolicy,
    technicalEligible,
    publishReady: technicalEligible,
    summary,
  };
}
